import { FC, RefObject, useEffect, useMemo, useRef, useState } from 'react';
// icons
import ArrowLeftIcon from '../../../../assets/icons/viewer-thumb-left.svg';
import ArrowRightIcon from '../../../../assets/icons/viewer-thumb-right.svg';
// types
import { IImagesList } from '../../entities';
// styles
import './styles.css';
import NoImageIcon from 'assets/broken-photo-placeholder.png';
import { getImageBySize } from 'utils/helpers';
import { Avatar, Grid } from '@mui/material';
import DefaultAvatar from 'components/default-avatar';

interface IThumbnailListProps {
  thumbnailList: IImagesList[];
  activeIndex: number;
  isKeyboardRef: RefObject<boolean>;
  handleThumbnailPress: (index: number) => void;
  isActivityFeedMedia?: boolean;
}

export const ThumbnailList: FC<IThumbnailListProps> = ({
  thumbnailList,
  activeIndex,
  isKeyboardRef,
  isActivityFeedMedia,
  handleThumbnailPress,
}) => {
  const photoWidth = 90;
  const gapWidth = 12;

  const containerRef = useRef<HTMLDivElement>(null);
  const [scrollPosition, setScrollPosition] = useState<number>(0);

  const prevIndexRef = useRef<number>(0);
  const mountOnce = useRef<boolean>(false);

  const isLeftDisabled = useMemo(
    () => scrollPosition === 0 || thumbnailList?.length === 1,
    [scrollPosition]
  );

  const isRightDisabled = useMemo(
    () =>
      scrollPosition >= containerRef.current?.scrollWidth! - containerRef.current?.clientWidth! ||
      thumbnailList?.length === 1,
    [scrollPosition, containerRef]
  );

  useEffect(() => {
    if (!mountOnce.current && activeIndex !== 0) {
      const newScrollPosition = Math.min(
        scrollPosition + (photoWidth * activeIndex + gapWidth * (activeIndex - 1)),
        containerRef?.current?.scrollWidth! - containerRef?.current?.clientWidth!
      );
      handleScroll(newScrollPosition);
      mountOnce.current = true;
    }
  }, [mountOnce.current]);

  useEffect(() => {
    if (!isKeyboardRef.current) {
      return;
    }

    if (prevIndexRef.current < activeIndex) {
      const newScrollPosition = Math.min(
        scrollPosition + (photoWidth + gapWidth),
        containerRef?.current?.scrollWidth! - containerRef?.current?.clientWidth!
      );
      handleScroll(newScrollPosition);
      prevIndexRef.current = activeIndex;
    } else if (prevIndexRef.current > activeIndex) {
      const newScrollPosition = Math.max(scrollPosition - (photoWidth + gapWidth), 0);
      handleScroll(newScrollPosition);
      prevIndexRef.current = activeIndex;
    }
  }, [activeIndex, isKeyboardRef, scrollPosition]);

  const handleScroll = (newScrollPosition: number) => {
    setScrollPosition(newScrollPosition);
    containerRef.current!.scrollTo({
      left: newScrollPosition,
      behavior: 'smooth',
    });
  };

  const handleLeft = () => {
    const newScrollPosition = Math.max(scrollPosition - (photoWidth + gapWidth), 0);
    handleScroll(newScrollPosition);
  };

  const handleRight = () => {
    const newScrollPosition = Math.min(
      scrollPosition + (photoWidth + gapWidth),
      containerRef?.current?.scrollWidth! - containerRef?.current?.clientWidth!
    );
    handleScroll(newScrollPosition);
  };

  const onThumbnailPress = (index: number) => {
    prevIndexRef.current = index;
    handleThumbnailPress(index);
  };

  return (
    <div className="thumbnail-list-container">
      {!isLeftDisabled ? (
        <div
          onClick={handleLeft}
          aria-disabled={isLeftDisabled}
          className={`thumbnail-arrow-left ${isLeftDisabled ? 'disabled' : ''}`}
        >
          <ArrowLeftIcon width={20} height={20} />
        </div>
      ) : null}
      <div className="thumbnail-list-wrapper" ref={containerRef}>
        <div className="thumbnail-list">
          {thumbnailList.map((item) => (
            <div className="thumbnail-item-wrapper">
              {isActivityFeedMedia && (
                <Grid
                  sx={{
                    position: 'absolute',
                    bottom: '6px',
                    left: '6px',
                    borderRadius: '4px',
                    overflow: 'hidden',
                    border: '1px solid #fff',
                  }}
                >
                  {item?.profile?.avatar?.url ? (
                    <Avatar
                      alt="avatar"
                      sx={{
                        width: '20px',
                        height: '20px',
                        borderRadius: '4px',
                      }}
                      src={getImageBySize(item?.profile?.avatar?.url, 20, 20)}
                      variant="circular"
                    />
                  ) : (
                    <DefaultAvatar
                      initials={`${item?.profile?.firstName?.[0]}${item?.profile?.lastName?.[0]}`}
                      variant="rounded"
                      alt="avatar"
                      sx={{
                        borderRadius: '4px',
                        width: '20px',
                        height: '20px',
                        fontSize: '12px',
                      }}
                    />
                  )}
                </Grid>
              )}

              <img
                onClick={() => onThumbnailPress(item.id)}
                key={item.id}
                src={item?.previewUrl ? item?.previewUrl : item.thumbnail}
                className={`thumbnail-item ${activeIndex === item.id ? 'isActive' : ''}`}
                onError={(e) => {
                  (e.currentTarget as HTMLImageElement).src = NoImageIcon;
                }}
              />
            </div>
          ))}
        </div>
      </div>
      {!isRightDisabled ? (
        <div
          onClick={handleRight}
          aria-disabled={isRightDisabled}
          className={`thumbnail-arrow-right ${isRightDisabled ? 'disabled' : ''}`}
        >
          <ArrowRightIcon width={20} height={20} />
        </div>
      ) : null}
    </div>
  );
};
